import React, { useState } from 'react';
import { ApolloError, useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import CheckIcon from '@mui/icons-material/Check';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  CompanyForm,
  CompanyFormValues,
} from 'src/components/Forms/CompanyForm';
import {
  CreateAccountForm,
  CreateAccountFormValues,
} from 'src/components/Forms/CreateAccountForm';
import {
  companyFormSchema,
  createAccountFormSchema,
} from 'src/components/Forms/validationSchemas';
import { Link } from 'src/components/Link/styled';
import { Modal } from 'src/components/Modals';
import { ROUTE_SOURCES } from 'src/constants/routeSources';
import { Advantage, Advantages } from 'src/pages/SignUp/styled';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import { REGISTER } from 'src/utils/gql/mutations';
import { Mutation, MutationRegisterArgs } from 'src/utils/gql/types';
import { setToken } from 'src/utils/localStorage';

const advantages = [
  '14-day free trial',
  'No credit card required',
  'Cancel anytime',
];

export const SignUp = () => {
  const snackbar = useSnackbar();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [showCompany, setShowCompany] = useState(false);

  const createAccountFormMethods = useForm<CreateAccountFormValues>({
    mode: 'onChange',
    resolver: yupResolver(createAccountFormSchema),
    criteriaMode: 'all',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      repeatPassword: '',
      policy: false,
    },
  });

  const companyformMethods = useForm<CompanyFormValues>({
    mode: 'onChange',
    resolver: yupResolver(companyFormSchema),
    criteriaMode: 'all',
    defaultValues: {
      name: '',
      industry: '',
      companyDescription: '',
    },
  });

  const { firstName, lastName, password, email } =
    createAccountFormMethods.getValues();

  const [signUp, { loading }] = useMutation<
    Pick<Mutation, 'register'>,
    MutationRegisterArgs
  >(REGISTER, {
    onCompleted: ({ register }) => {
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Lead', {
          content_name: 'signup_form',
          source: 'landing',
        });
      }
      // ✅ Salva il token e autentica automaticamente l'utente
      setToken(register);
      snackbar('Account created successfully!', 'success');
      navigate(`/${ROUTE_SOURCES.CONNECTORS}`);
    },
    onError: (err) => {
      const { graphQLErrors, message: errorText } = err as ApolloError;
      const message =
        graphQLErrors && graphQLErrors.length > 0
          ? graphQLErrors[0].message
          : errorText;

      snackbar(message);
    },
  });

  const handleClickNext = () => setShowCompany(true);
  const handleClickBack = () => setShowCompany(false);

  const handleSubmit: SubmitHandler<CompanyFormValues> = ({
    name,
    industry,
    companyDescription,
    usageOptions,
  }) => {
    signUp({
      variables: {
        registerData: {
          firstName,
          lastName,
          email,
          password,
          companyData: {
            name,
            industry,
            companyDescription,
            usageOptions: [usageOptions],
          },
        },
      },
    });
  };

  return (
    <Modal
      open
      title="Kipify"
      onBack={showCompany ? handleClickBack : undefined}
      hideBackdrop
    >
      {showCompany ? (
        <Box width="410px" maxWidth="100%">
          <Typography
            variant="h2"
            textAlign="center"
            mb={isSmallScreen ? '25px' : '40px'}
          >
            Set up your Company
          </Typography>
          <FormProvider {...companyformMethods}>
            <CompanyForm
              onSubmit={handleSubmit}
              buttonLoading={loading}
              isRegistrationForm={true}
            />
          </FormProvider>
        </Box>
      ) : (
        <>
          <Typography variant="h2" textAlign="center">
            Create Your Account
          </Typography>
          <Advantages>
            {advantages.map((advantage, index) => (
              <Advantage key={index}>
                <CheckIcon />
                <Typography variant="body2">{advantage}</Typography>
              </Advantage>
            ))}
          </Advantages>
          <FormProvider {...createAccountFormMethods}>
            <CreateAccountForm onClick={handleClickNext} />
          </FormProvider>
          <Typography textAlign="center" variant="body2" mt="20px">
            Already have an account?
            <Link to={`/${ROUTE_SOURCES.LOGIN}`}> Sign In</Link>
          </Typography>
        </>
      )}
    </Modal>
  );
};
